import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "../lib/utils";

/** Caller-supplied (i18n-resolved) labels — the package stays i18n-free (D-20). */
export interface PaginationLabels {
  /** Accessible name for the nav landmark. */
  nav: string;
  previous: string;
  next: string;
  /** Accessible name for a numbered page button, given the page number. */
  page: (page: number) => string;
}

export interface PaginationProps {
  /** Current page (1-based). */
  page: number;
  /** Total page count. */
  pageCount: number;
  onNavigate: (page: number) => void;
  labels: PaginationLabels;
  /** Numbered pages shown on each side of the current page (default 1). */
  siblingCount?: number;
  className?: string;
}

/**
 * Windowed page list: always the first + last page, the current page, and
 * `siblingCount` neighbours on each side, with "ellipsis" markers filling the
 * gaps. Pure — the single non-trivial branch is covered by pagination.test.tsx.
 */
export function paginationRange(
  page: number,
  pageCount: number,
  siblingCount = 1,
): (number | "ellipsis")[] {
  const range = (start: number, end: number) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

  // first + last + current + a sibling window + up to two ellipsis slots.
  if (siblingCount + 5 >= pageCount) return range(1, pageCount);

  const left = Math.max(page - siblingCount, 1);
  const right = Math.min(page + siblingCount, pageCount);
  // Collapse to an ellipsis only when it hides more than one page.
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < pageCount - 1;

  const out: (number | "ellipsis")[] = [1];
  if (showLeftEllipsis) out.push("ellipsis");
  else out.push(...range(2, left - 1));
  out.push(...range(left, right).filter((p) => p !== 1 && p !== pageCount));
  if (showRightEllipsis) out.push("ellipsis");
  else out.push(...range(right + 1, pageCount - 1));
  out.push(pageCount);
  return out;
}

// ponytail: `border-border` hairline — no `--border-strong` token exists yet
// (UI-SPEC names it but plan 01 didn't ship it). Swap when that token lands.
const pageButton =
  "inline-flex h-8 min-w-8 items-center justify-center rounded-badge border border-border px-2 text-sm font-medium transition-colors duration-150 motion-reduce:transition-none hover:bg-muted focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none aria-disabled:pointer-events-none aria-disabled:opacity-50";

/**
 * Atlas asymmetric-corner Pagination (audit D2). A nav landmark of prev/next +
 * numbered page buttons on the `rounded-badge` corner pair; the active page is
 * an ink fill (`bg-foreground text-background`) per the segmented "active = ink"
 * language and carries aria-current. Prev/next disable at the boundaries via
 * aria-disabled (kept focusable, no onNavigate fired). Presentational — all
 * labels arrive as props; text-only children (XSS control T-09.3-12).
 */
export function Pagination({
  page,
  pageCount,
  onNavigate,
  labels,
  siblingCount = 1,
  className,
}: PaginationProps) {
  const atStart = page <= 1;
  const atEnd = page >= pageCount;

  const go = (target: number) => {
    if (target < 1 || target > pageCount || target === page) return;
    onNavigate(target);
  };

  return (
    <nav aria-label={labels.nav} data-slot="pagination" className={className}>
      <ul className="flex items-center gap-1">
        <li>
          <button
            type="button"
            aria-label={labels.previous}
            aria-disabled={atStart || undefined}
            onClick={() => go(page - 1)}
            className={cn(pageButton, "gap-1 pl-1.5")}
          >
            <ChevronLeftIcon className="size-4" />
          </button>
        </li>

        {paginationRange(page, pageCount, siblingCount).map((item, i) =>
          item === "ellipsis" ? (
            <li key={`ellipsis-${i}`} aria-hidden="true">
              <span className="text-muted-foreground flex h-8 w-8 items-center justify-center">
                …
              </span>
            </li>
          ) : (
            <li key={item}>
              <button
                type="button"
                aria-label={labels.page(item)}
                aria-current={item === page ? "page" : undefined}
                onClick={() => go(item)}
                className={cn(
                  pageButton,
                  item === page &&
                    "bg-foreground text-background border-foreground hover:bg-foreground",
                )}
              >
                {item}
              </button>
            </li>
          ),
        )}

        <li>
          <button
            type="button"
            aria-label={labels.next}
            aria-disabled={atEnd || undefined}
            onClick={() => go(page + 1)}
            className={cn(pageButton, "gap-1 pr-1.5")}
          >
            <ChevronRightIcon className="size-4" />
          </button>
        </li>
      </ul>
    </nav>
  );
}
